import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Plus, Trash2, Play, RefreshCw, Edit3, Copy, X, Loader2,
  Monitor, FileText, Upload, Share2, Download, CopyPlus, Globe, Lock
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const THEMES = [
  { value: 'ministry', label: 'Ministry', bg: '#1B3A6B', text: '#fff' },
  { value: 'dark', label: 'Dark', bg: '#0F1A2A', text: '#fff' },
  { value: 'light', label: 'Light', bg: '#FFFFFF', text: '#0F1A2A' },
  { value: 'warm', label: 'Warm', bg: '#FDF8F0', text: '#2D1B00' },
  { value: 'modern', label: 'Modern', bg: '#18181B', text: '#FAFAFA' },
];

export default function PresentationManager({ onEdit, onPresent }) {
  const [presentations, setPresentations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', theme: 'ministry' });
  const [creating, setCreating] = useState(false);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/presentation/list`);
      setPresentations(res.data.presentations);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const handleCreate = async () => {
    if (!form.title.trim()) return;
    setCreating(true);
    try {
      const res = await axios.post(`${API}/presentation/create`, form);
      setShowCreate(false);
      setForm({ title: '', description: '', theme: 'ministry' });
      onEdit?.(res.data.id);
    } catch (err) { alert(err.response?.data?.detail || 'Failed'); }
    finally { setCreating(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this presentation?')) return;
    try { await axios.delete(`${API}/presentation/${id}`); load(); }
    catch (err) { alert('Failed'); }
  };

  const handleDuplicate = async (id) => {
    try { await axios.post(`${API}/presentation/${id}/duplicate`); load(); }
    catch (err) { alert('Failed'); }
  };

  const togglePublic = async (p) => {
    try {
      await axios.put(`${API}/presentation/${p.id}`, { is_public: !p.is_public });
      load();
    } catch (err) { alert('Failed'); }
  };

  const exportPPTX = (id) => {
    window.open(`${API}/presentation/${id}/export`, '_blank');
  };

  const copyShareLink = (code) => {
    navigator.clipboard.writeText(`${window.location.origin}/present/${code}`);
    alert('Share link copied!');
  };

  return (
    <div data-testid="presentation-manager">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-heading text-2xl font-bold text-[#1B3A6B]">Presentations</h2>
          <p className="text-sm text-[#475569] mt-1">Create Prezi-style presentations with cool transitions</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowCreate(true)} className="btn-primary py-2 px-4 rounded-lg font-medium flex items-center gap-2 text-sm" data-testid="create-pres-btn">
            <Plus className="w-4 h-4" /> New Presentation
          </button>
          <button onClick={load} className="p-2 rounded-lg border border-[#E2DFD8] hover:bg-[#F4F2EC]">
            <RefreshCw className="w-4 h-4 text-[#475569]" />
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-16"><Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" /><p className="text-[#475569]">Loading...</p></div>
      ) : presentations.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-xl shadow-sm">
          <Monitor className="w-12 h-12 text-[#94A3B8] mx-auto mb-3" />
          <p className="text-[#475569] mb-4">No presentations yet</p>
          <button onClick={() => setShowCreate(true)} className="btn-primary py-2 px-6 rounded-lg font-medium text-sm">Create Presentation</button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {presentations.map(p => (
            <div key={p.id} className="bg-white rounded-xl shadow-sm border border-[#E2DFD8] overflow-hidden hover:shadow-md transition-shadow" data-testid={`pres-card-${p.id}`}>
              {/* Theme preview bar */}
              <div className="h-24 flex items-center justify-center relative" style={{ backgroundColor: THEMES.find(t => t.value === p.theme)?.bg || '#1B3A6B' }}>
                <span className="text-lg font-bold" style={{ color: THEMES.find(t => t.value === p.theme)?.text || '#fff' }}>{p.title}</span>
                <span className="absolute top-2 right-2 text-xs bg-black/30 text-white px-2 py-0.5 rounded">{p.slide_count} slides</span>
              </div>
              <div className="p-4">
                {p.description && <p className="text-xs text-[#475569] mb-3 line-clamp-2">{p.description}</p>}
                
                {/* Share link */}
                {p.is_public && (
                  <div className="flex items-center gap-1 mb-3">
                    <Globe className="w-3 h-3 text-green-500" />
                    <button onClick={() => copyShareLink(p.share_code)} className="text-xs text-[#1B3A6B] hover:underline truncate">{window.location.origin}/present/{p.share_code}</button>
                  </div>
                )}

                <div className="flex items-center gap-1.5">
                  <button onClick={() => onEdit?.(p.id)} className="flex-1 py-2 bg-[#1B3A6B] hover:bg-[#254B8A] text-white rounded-lg text-xs font-medium flex items-center justify-center gap-1 transition-colors" data-testid={`edit-pres-${p.id}`}>
                    <Edit3 className="w-3.5 h-3.5" /> Edit
                  </button>
                  {p.slide_count > 0 && (
                    <button onClick={() => onPresent?.(p.id)} className="py-2 px-3 bg-green-600 hover:bg-green-700 text-white rounded-lg text-xs font-medium transition-colors" data-testid={`present-${p.id}`}>
                      <Play className="w-3.5 h-3.5" />
                    </button>
                  )}
                  <button onClick={() => togglePublic(p)} className={`py-2 px-2 rounded-lg text-xs border transition-colors ${p.is_public ? 'bg-green-50 border-green-200 text-green-600' : 'border-[#E2DFD8] text-[#94A3B8] hover:text-[#475569]'}`} title={p.is_public ? 'Public' : 'Private'}>
                    {p.is_public ? <Globe className="w-3.5 h-3.5" /> : <Lock className="w-3.5 h-3.5" />}
                  </button>
                  <button onClick={() => exportPPTX(p.id)} className="py-2 px-2 border border-[#E2DFD8] rounded-lg text-[#475569] hover:bg-[#F4F2EC]" title="Export PPTX">
                    <Download className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={() => handleDuplicate(p.id)} className="py-2 px-2 border border-[#E2DFD8] rounded-lg text-blue-600 hover:bg-blue-50" title="Duplicate">
                    <CopyPlus className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={() => handleDelete(p.id)} className="py-2 px-2 border border-[#E2DFD8] rounded-lg text-red-500 hover:bg-red-50" title="Delete">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Modal */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowCreate(false)}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6" onClick={e => e.stopPropagation()} data-testid="create-pres-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-[#0F1A2A]">New Presentation</h3>
              <button onClick={() => setShowCreate(false)} className="p-1 hover:bg-[#F4F2EC] rounded-lg"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[#0F1A2A] mb-1">Title</label>
                <input type="text" value={form.title} onChange={e => setForm({...form, title: e.target.value})} placeholder="e.g., Sunday Sermon Slides" className="w-full px-3 py-2 border border-[#E2DFD8] rounded-lg focus:ring-2 focus:ring-[#1B3A6B]" data-testid="pres-title-input" />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#0F1A2A] mb-1">Description</label>
                <textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} placeholder="Optional..." rows={2} className="w-full px-3 py-2 border border-[#E2DFD8] rounded-lg" />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#0F1A2A] mb-2">Theme</label>
                <div className="flex gap-2">
                  {THEMES.map(t => (
                    <button key={t.value} onClick={() => setForm({...form, theme: t.value})} className={`flex-1 py-3 rounded-lg text-xs font-medium border-2 transition-all ${form.theme === t.value ? 'border-[#B8860B] scale-105' : 'border-transparent'}`} style={{ backgroundColor: t.bg, color: t.text }}>
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>
              <button onClick={handleCreate} disabled={creating || !form.title.trim()} className="w-full btn-primary py-2.5 rounded-lg font-medium disabled:opacity-50 flex items-center justify-center gap-2" data-testid="create-pres-submit">
                {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                {creating ? 'Creating...' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
